/**
 * Pure shaping of assistant-service chat history into the panel's view model.
 *
 * The wire format is awkward for a UI: `answer` is a JSON *string* holding the
 * markdown, the progress messages, and the preliminary flag, and history comes
 * back as an undifferentiated list of every turn on the case. Keeping the
 * unpacking here means the panel component never parses JSON or reasons about
 * record status.
 */

import type { MobileEvidence } from '@/shared/types';

/** One chat-history row as stored by assistant-service. */
export interface AssistantHistoryRow {
  question?: string;
  answer?: string;
  status?: string;
  epoch_time_secs?: number;
  chat_key?: string;
  session_id?: string;
  request_type?: string;
}

/**
 * A step the agent emitted while working.
 *
 * `type` is deliberately a plain string: assistant-service builds these as bare
 * dicts with no shared schema, so a new step kind is a normal occurrence rather
 * than something to break on.
 */
export interface AssistantProgressMessage {
  id: string;
  type: string;
  content: string;
  timestamp?: string;
}

export interface AssistantTurn {
  /** Server turn id. Negative for a turn that hasn't been acknowledged yet. */
  epochTimeSecs: number;
  question: string;
  /** Final markdown answer. Empty while the agent is still working. */
  answer: string;
  messages: AssistantProgressMessage[];
  /** True while the answer may still change. */
  pending: boolean;
  failed: boolean;
  sessionId?: string;
  /** Cited queries from this turn, used to show what the agent asked. */
  citations: Array<{ evidence?: MobileEvidence }>;
}

/** The placeholder assistant-service returns before the agent has produced anything. */
const PRELIMINARY_OUTPUT = /^\s*Analyzing documents/i;

interface ParsedAnswer {
  output: string;
  messages: AssistantProgressMessage[];
  isPreliminary: boolean;
  citations: Array<{ evidence?: MobileEvidence }>;
}

function parseAnswer(raw: string | undefined): ParsedAnswer {
  if (!raw) return { output: '', messages: [], isPreliminary: false, citations: [] };

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    // Older rows (and error paths) store the answer as bare text.
    return { output: raw, messages: [], isPreliminary: false, citations: [] };
  }

  const output = typeof parsed.output === 'string' ? parsed.output : '';
  return {
    output: PRELIMINARY_OUTPUT.test(output) ? '' : output,
    messages: normalizeMessages(parsed.messages),
    isPreliminary: parsed.is_preliminary === true,
    citations: normalizeCitations(parsed.citations),
  };
}

function normalizeCitations(raw: unknown): Array<{ evidence?: MobileEvidence }> {
  if (!Array.isArray(raw)) return [];
  const citations: Array<{ evidence?: MobileEvidence }> = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const evidence = (entry as { evidence?: unknown }).evidence;
    if (!evidence || typeof evidence !== 'object') continue;
    citations.push({ evidence: evidence as MobileEvidence });
  }
  return citations;
}

function normalizeMessages(raw: unknown): AssistantProgressMessage[] {
  if (!Array.isArray(raw)) return [];
  const messages: AssistantProgressMessage[] = [];
  for (const [index, entry] of raw.entries()) {
    if (!entry || typeof entry !== 'object') continue;
    const message = entry as Record<string, unknown>;
    const content = typeof message.content === 'string' ? message.content.trim() : '';
    if (!content) continue;
    messages.push({
      id: typeof message.id === 'string' ? message.id : `msg_${index}`,
      type: typeof message.type === 'string' ? message.type : 'progress_update',
      content,
      timestamp: typeof message.timestamp === 'string' ? message.timestamp : undefined,
    });
  }
  return messages;
}

/**
 * Turns for this panel, oldest first.
 *
 * History is per case+user, not per acquisition, so a case with both a regular
 * RAG conversation and mobile analyses would otherwise mix them into one thread.
 * Filtering on `request_type` keeps the panel showing only its own work.
 */
export function buildAssistantTurns(rows: unknown): AssistantTurn[] {
  if (!Array.isArray(rows)) return [];

  const turns: AssistantTurn[] = [];
  for (const entry of rows) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as AssistantHistoryRow;
    if (row.request_type !== 'mobile') continue;

    const { output, messages, isPreliminary, citations } = parseAnswer(row.answer);
    const status = row.status ?? 'unknown';
    turns.push({
      epochTimeSecs: Number(row.epoch_time_secs ?? 0),
      question: row.question ?? '',
      answer: output,
      messages,
      pending:
        status === 'initializing' ||
        status === 'inprogress' ||
        (isPreliminary && status !== 'error'),
      failed: status === 'error',
      sessionId: row.session_id,
      citations,
    });
  }

  return turns.sort((a, b) => a.epochTimeSecs - b.epochTimeSecs);
}

/** Progress step kinds worth showing, in the order they're most useful. */
const STEP_LABELS: Record<string, string> = {
  reasoning: 'Thinking',
  tool_execution: 'Running query',
  progress_update: 'Working',
  agent_response: 'Writing',
};

export function progressStepLabel(type: string): string {
  return STEP_LABELS[type] ?? 'Working';
}

/**
 * Rewrite artifact-type mentions in an answer as workspace links.
 *
 * Mobile answers are plain prose that names artifact types — assistant-service
 * deliberately emits no citation structure for them — so this matches display
 * names against the ui-config catalog. It is a convenience, not a citation
 * contract: a name that also reads as an ordinary English word will be missed,
 * and nothing here asserts the agent actually read that type.
 *
 * Matching is longest-name-first so "Chat Messages" doesn't get clipped to
 * "Chat", and code spans and existing links are left alone.
 */
export function linkifyArtifactMentions(
  markdown: string,
  artifacts: { graphqlType: string; displayName: string }[],
  toHref: (graphqlType: string) => string,
): string {
  if (!markdown) return markdown;

  const named = artifacts
    .filter((a) => a.displayName.trim().length >= 4)
    .sort((a, b) => b.displayName.length - a.displayName.length);
  if (!named.length) return markdown;

  const byName = new Map<string, string>();
  for (const artifact of named) {
    const key = artifact.displayName.toLowerCase();
    if (!byName.has(key)) byName.set(key, artifact.graphqlType);
  }

  const pattern = new RegExp(
    `\\b(${named.map((a) => escapeRegExp(a.displayName)).join('|')})\\b`,
    'gi',
  );

  return mapOutsideCodeAndLinks(markdown, (segment) =>
    segment.replace(pattern, (match) => {
      const graphqlType = byName.get(match.toLowerCase());
      return graphqlType ? `[${match}](${toHref(graphqlType)})` : match;
    }),
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Apply `transform` only to prose.
 *
 * Rewriting inside a fenced block would corrupt a query the agent is showing
 * its work with, and rewriting inside an existing link would nest brackets.
 */
function mapOutsideCodeAndLinks(
  markdown: string,
  transform: (segment: string) => string,
): string {
  const protectedSpans = /(```[\s\S]*?```|`[^`\n]*`|\[[^\]]*\]\([^)]*\))/g;
  const parts = markdown.split(protectedSpans);
  return parts
    .map((part, index) => (index % 2 === 1 ? part : transform(part)))
    .join('');
}
